import { useEffect, useState, useCallback } from "react";
import { COUNTRIES } from "../data/content";

const SUPPORTED = new Set(COUNTRIES.map((c) => c.code));

// Map ISO alpha-2 country codes returned by ipapi.co to the codes we use
// internally. Countries without a supported store fall through to the fallback.
const ISO_TO_INTERNAL = {
  US: "US",
  GB: "UK",
  CA: "CA",
  JP: "JP",
  AU: "AU",
  IN: "IN",
  BR: "BR",
  IT: "IT",
  FR: "FR",
  DE: "DE",
  ES: "ES",
  DK: "DK",
  NL: "NL",
  MX: "MX",
  SE: "SE",
  BE: "BE",
};

const LS_KEY = "bflh:country";

export default function useCountry() {
  const [country, setCountryState] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | detected | manual | default | unknown

  const setCountry = useCallback((code, mode = "manual") => {
    if (!SUPPORTED.has(code)) return;
    setCountryState(code);
    setStatus(mode);
    try {
      localStorage.setItem(LS_KEY, code);
    } catch (e) {
      // ignore
    }
  }, []);

  const clearCountry = useCallback(() => {
    setCountryState(null);
    setStatus("unknown");
    try {
      localStorage.removeItem(LS_KEY);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    let savedCountry = null;

    // Keep a manual choice ready as a fallback, but always try live location first.
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved && SUPPORTED.has(saved)) {
        savedCountry = saved;
      }
    } catch (e) {
      // ignore
    }

    const applyFallback = () => {
      if (cancelled) return;
      if (savedCountry) {
        setCountryState(savedCountry);
        setStatus("manual");
        return;
      }
      setCountryState("CA");
      setStatus("default");
    };

    // Always try IP geolocation on page load/refresh before using a saved choice.
    (async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error("ipapi failed");
        const data = await res.json();
        if (cancelled) return;
        const mapped = ISO_TO_INTERNAL[data?.country_code];
        if (mapped) {
          setCountryState(mapped);
          setStatus("detected");
          return;
        }
        applyFallback();
      } catch (e) {
        applyFallback();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const meta = COUNTRIES.find((c) => c.code === country) || null;
  return { country, meta, status, setCountry, clearCountry };
}
